import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import Layout from "../Layout";
import superAdminAPI from "../services/superAdminAPI";
import {
    Activity,
    AlertTriangle,
    Bug,
    Building2,
    CheckCircle,
    Clock,
    Database,
    Mail,
    RefreshCw,
    Server,
    UserCog,
    UserPlus,
    Users,
    XCircle
} from "lucide-react";

const HomePage = () => {
    const [dashboardStats, setDashboardStats] = useState({
        total_schools: 0,
        active_schools: 0,
        trial_schools: 0,
        suspended_schools: 0,
        total_users: 0,
        total_admins: 0,
        recent_signups: 0,
        system_health: {
            database: false,
            api: false
        }
    });

    const [systemHealth, setSystemHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const navigate = useNavigate();

    // Debug state
    const [debugInfo, setDebugInfo] = useState({
        apiConnected: false,
        lastError: null,
        apiUrl: superAdminAPI.baseURL,
        isAuthenticated: superAdminAPI.isAuthenticated()
    });

    const [showDebug, setShowDebug] = useState(process.env.REACT_APP_DEBUG === 'true');

    useEffect(() => {
        // First check authentication
        if (!superAdminAPI.isAuthenticated()) {
            navigate('/?expired=true');
            return;
        }

        fetchDashboardData();
    }, [navigate]);

    const fetchDashboardData = async () => {
        setLoading(true);
        setError(null);

        try {
            // First test connectivity
            const connectionTest = await superAdminAPI.testConnection();
            setDebugInfo(prev => ({
                ...prev,
                apiConnected: connectionTest.success,
                lastError: connectionTest.success ? null : connectionTest.error,
                isAuthenticated: superAdminAPI.isAuthenticated()
            }));

            if (!connectionTest.success) {
                console.error("Backend connectivity failed:", connectionTest.error);
                setError(`Backend connection failed: ${connectionTest.error}`);
                setLoading(false);
                return;
            }

            // Then fetch dashboard stats
            const [dashboardData, healthData] = await Promise.all([
                superAdminAPI.getDashboardStats(),
                superAdminAPI.getSystemHealth()
            ]);

            setDashboardStats(dashboardData);
            setSystemHealth(healthData);
            setDebugInfo(prev => ({...prev, lastError: null}));
            console.log('Dashboard data loaded successfully:', dashboardData);
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
            const errorMessage = superAdminAPI.handleError(error);
            setError(errorMessage);
            setDebugInfo(prev => ({...prev, lastError: errorMessage}));

            // If session expired, redirect to login
            if (errorMessage.includes('Session expired')) {
                navigate('/?expired=true');
            }
        } finally {
            setLoading(false);
        }
    };

    const refreshData = async () => {
        setRefreshing(true);
        setError(null);

        try {
            const [dashboardData, healthData] = await Promise.all([
                superAdminAPI.getDashboardStats(),
                superAdminAPI.getSystemHealth()
            ]);

            setDashboardStats(dashboardData);
            setSystemHealth(healthData);
            setDebugInfo(prev => ({...prev, lastError: null}));
        } catch (error) {
            console.error("Error refreshing dashboard data:", error);
            const errorMessage = superAdminAPI.handleError(error);
            setError(errorMessage);
            setDebugInfo(prev => ({...prev, lastError: errorMessage}));
        } finally {
            setRefreshing(false);
        }
    };

    const statsCards = [
        {
            icon: <Building2 size={24}/>,
            title: "Total Schools",
            value: dashboardStats.total_schools,
            color: "from-blue-500 to-blue-600",
            bgColor: "bg-blue-50",
            iconColor: "text-blue-600"
        },
        {
            icon: <Activity size={24}/>,
            title: "Active Schools",
            value: dashboardStats.active_schools,
            color: "from-green-500 to-green-600",
            bgColor: "bg-green-50",
            iconColor: "text-green-600"
        },
        {
            icon: <Clock size={24}/>,
            title: "Trial Schools",
            value: dashboardStats.trial_schools,
            color: "from-yellow-500 to-yellow-600",
            bgColor: "bg-yellow-50",
            iconColor: "text-yellow-600"
        },
        {
            icon: <AlertTriangle size={24}/>,
            title: "Suspended Schools",
            value: dashboardStats.suspended_schools,
            color: "from-red-500 to-red-600",
            bgColor: "bg-red-50",
            iconColor: "text-red-600"
        },
        {
            icon: <Users size={24}/>,
            title: "Total Users",
            value: dashboardStats.total_users,
            color: "from-purple-500 to-purple-600",
            bgColor: "bg-purple-50",
            iconColor: "text-purple-600"
        },
        {
            icon: <UserCog size={24}/>,
            title: "Total Admins",
            value: dashboardStats.total_admins,
            color: "from-indigo-500 to-indigo-600",
            bgColor: "bg-indigo-50",
            iconColor: "text-indigo-600"
        },
        {
            icon: <UserPlus size={24}/>,
            title: "Recent Signups",
            value: dashboardStats.recent_signups,
            color: "from-orange-500 to-orange-600",
            bgColor: "bg-orange-50",
            iconColor: "text-orange-600"
        }
    ];

    // Loading state
    if (loading) {
        return (
            <Layout pageTitle="Edumonics Super Admin Dashboard">
                <div className="min-h-screen p-6 flex items-center justify-center">
                    <div className="text-center">
                        <div
                            className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading dashboard...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    // Error state
    if (error) {
        return (
            <Layout pageTitle="Edumonics Super Admin Dashboard">
                <div className="min-h-screen p-6 flex items-center justify-center">
                    <div className="text-center max-w-md">
                        <div
                            className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <XCircle size={32} className="text-red-600"/>
                        </div>
                        <h2 className="text-xl font-semibold text-gray-800 mb-2">
                            Dashboard Error
                        </h2>
                        <p className="text-gray-600 mb-6">
                            {error}
                        </p>
                        <div className="space-y-3">
                            <button
                                onClick={() => refreshData()}
                                className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors mr-3"
                            >
                                Retry
                            </button>
                            {(error.includes('Session expired') || error.includes('Please login')) && (
                                <button
                                    onClick={() => navigate('/')}
                                    className="bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors"
                                >
                                    Go to Login
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle="Edumonics Super Admin Dashboard">
            <div className="min-h-screen p-6">
                <div className="max-w-7xl mx-auto space-y-8">
                    {/* Welcome Header */}
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                        <div className="text-left">
                            <h1 className="text-4xl font-bold text-slate-800 mb-2 flex items-center gap-3">
                                Super Admin Dashboard
                                <span className="text-2xl">🚀</span>
                            </h1>
                            <p className="text-lg text-slate-600">
                                Manage multiple schools and monitor the entire multi-tenant platform.
                            </p>
                            {!showDebug && process.env.REACT_APP_DEBUG === 'true' && (
                                <button
                                    onClick={() => setShowDebug(true)}
                                    className="mt-2 text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                                >
                                    <Bug size={12}/> Show Debug Info
                                </button>
                            )}
                        </div>

                        <button
                            onClick={refreshData}
                            disabled={refreshing}
                            className="px-4 py-2 bg-orange-100 text-orange-600 rounded-lg flex items-center gap-2 hover:bg-orange-200 transition-colors"
                        >
                            {refreshing ? (
                                <>
                                    <RefreshCw size={16} className="animate-spin"/>
                                    Refreshing...
                                </>
                            ) : (
                                <>
                                    <RefreshCw size={16}/>
                                    Refresh Data
                                </>
                            )}
                        </button>
                    </div>

                    {/* Debug Panel */}
                    {showDebug && (
                        <div className="mb-6 p-4 bg-gray-100 rounded-lg border">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                    <Bug className="text-orange-500" size={16}/>
                                    Debug Information
                                </h3>
                                <button
                                    onClick={() => setShowDebug(false)}
                                    className="text-gray-500 hover:text-gray-700 text-sm"
                                >
                                    Hide
                                </button>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                                <div>
                                    <span className="font-medium">API URL:</span>
                                    <div className="text-gray-600 break-all">{debugInfo.apiUrl}</div>
                                </div>
                                <div>
                                    <span className="font-medium">API Connected:</span>
                                    <div className={debugInfo.apiConnected ? 'text-green-600' : 'text-red-600'}>
                                        {debugInfo.apiConnected ? '✅ Yes' : '❌ No'}
                                    </div>
                                </div>
                                <div>
                                    <span className="font-medium">Authenticated:</span>
                                    <div className={debugInfo.isAuthenticated ? 'text-green-600' : 'text-red-600'}>
                                        {debugInfo.isAuthenticated ? '✅ Yes' : '❌ No'}
                                    </div>
                                </div>
                                <div>
                                    <span className="font-medium">Last Error:</span>
                                    <div className="text-red-600 break-all">
                                        {debugInfo.lastError || 'None'}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                <button
                                    onClick={async () => {
                                        try {
                                            const test = await superAdminAPI.testConnection();
                                            setDebugInfo(prev => ({
                                                ...prev,
                                                apiConnected: test.success,
                                                lastError: test.success ? null : test.error
                                            }));
                                            alert(test.success ? 'Connection successful!' : `Connection failed: ${test.error}`);
                                        } catch (error) {
                                            alert(`Test failed: ${error.message}`);
                                        }
                                    }}
                                    className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                                >
                                    Test Connection
                                </button>
                                <button
                                    onClick={async () => {
                                        try {
                                            const data = await superAdminAPI.getDashboardStats();
                                            setDashboardStats(data);
                                            setError(null);
                                            alert('Dashboard stats fetched successfully!');
                                        } catch (error) {
                                            const errorMsg = superAdminAPI.handleError(error);
                                            setError(errorMsg);
                                            alert(`Failed to fetch stats: ${errorMsg}`);
                                        }
                                    }}
                                    className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                                >
                                    Test Dashboard API
                                </button>
                                <button
                                    onClick={() => {
                                        const token = localStorage.getItem('accessToken');
                                        console.log('Current token:', token);
                                        alert(`Token: ${token ? 'Present' : 'Missing'}\nLength: ${token ? token.length : 0}`);
                                    }}
                                    className="px-3 py-1 bg-purple-500 text-white text-xs rounded hover:bg-purple-600"
                                >
                                    Check Token
                                </button>
                                <button
                                    onClick={() => {
                                        superAdminAPI.logout();
                                    }}
                                    className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                                >
                                    Test Logout
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Statistics Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {statsCards.map((card, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div
                                        className={`w-12 h-12 ${card.bgColor} rounded-xl flex items-center justify-center`}>
                                        <span className={card.iconColor}>
                                            {card.icon}
                                        </span>
                                    </div>
                                    <div
                                        className={`px-3 py-1 bg-gradient-to-r ${card.color} text-white text-xs font-medium rounded-full`}>
                                        Live
                                    </div>
                                </div>
                                <h3 className="text-gray-600 text-sm font-medium mb-1">{card.title}</h3>
                                <p className="text-2xl font-bold text-gray-800">{card.value}</p>
                            </div>
                        ))}
                    </div>

                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                        <h2 className="text-xl font-semibold text-slate-800 mb-6 flex items-center gap-2">
                            <Activity size={24} className="text-orange-600"/>
                            System Health Status
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {[
                                {
                                    name: "Database",
                                    status: dashboardStats?.system_health?.database || false,
                                    icon: <Database size={20}/>,
                                    description: "MongoDB Cluster"
                                },
                                {
                                    name: "API Gateway",
                                    status: dashboardStats?.system_health?.api || false,
                                    icon: <Activity size={20}/>,
                                    description: "REST API"
                                },
                                {
                                    name: "Email Service",
                                    status: dashboardStats?.system_health?.email || false,
                                    icon: <Mail size={20}/>,
                                    description: "SMTP Service"
                                }
                            ].map((service, index) => (
                                <div
                                    key={index}
                                    className={`
                                        p-4 rounded-xl border-2 transition-all duration-200
                                        ${service.status
                                        ? 'bg-green-50 border-green-200 hover:bg-green-100'
                                        : 'bg-red-50 border-red-200 hover:bg-red-100'
                                    }
                                    `}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <span className={service.status ? 'text-green-600' : 'text-red-600'}>
                                                {service.icon}
                                            </span>
                                            <h3 className="font-medium text-gray-800 text-sm">{service.name}</h3>
                                        </div>
                                        {service.status ? (
                                            <CheckCircle size={16} className="text-green-600"/>
                                        ) : (
                                            <XCircle size={16} className="text-red-600"/>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-600 mb-2">{service.description}</p>
                                    <div className={`
                                        px-2 py-1 rounded-full text-xs font-medium
                                        ${service.status
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-red-100 text-red-700'
                                    }
                                    `}>
                                        {service.status ? 'Healthy' : 'Down'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* System Health Details (if available) */}
                    {systemHealth && (
                        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                            <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                <Server size={24} className="text-orange-600"/>
                                System Details
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-gray-50 p-4 rounded-xl">
                                    <h3 className="text-sm font-medium text-gray-700 mb-2">Database Status</h3>
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">Connection Status:</span>
                                            <span
                                                className={systemHealth.database_connection === "healthy" ? "text-green-600" : "text-red-600"}>
                                                {systemHealth.database_connection || "Unknown"}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-xl">
                                    <h3 className="text-sm font-medium text-gray-700 mb-2">API Status</h3>
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">Status:</span>
                                            <span className="text-green-600">
                                                {systemHealth.api_status || "Unknown"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4 text-xs text-gray-500">
                                Last refreshed: {new Date().toLocaleString()}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default HomePage;